import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import type { UserRole } from '../hooks/useAuth';

interface RoleGateProps {
  /** Required role. If user.role doesn't match → redirect. */
  role: NonNullable<UserRole>;
  children: React.ReactNode;
  /** Where to redirect on failure. Default: '/' */
  redirectTo?: string;
}

/**
 * RoleGate — blocks access to children if the authenticated user
 * doesn't have the required role.
 *
 * Usage:
 *   <RoleGate role="owner"><AddListing /></RoleGate>
 *
 * Behaviour:
 *  - If not loaded yet → render nothing (prevents flash)
 *  - If not signed in → let AuthGate handle it (don't double-redirect)
 *  - If role is null (not yet set) → allow through (graceful degradation)
 *  - If role doesn't match → redirect to redirectTo with state flag
 */
export const RoleGate: React.FC<RoleGateProps> = ({
  role,
  children,
  redirectTo = '/',
}) => {
  const { user, isLoaded, isSignedIn } = useAuth();

  // Still loading — don't flash a redirect
  if (!isLoaded) return null;

  // Not signed in — let AuthGate (wrapping us) handle it
  if (!isSignedIn) return <>{children}</>;

  // Role not yet resolved (pre-migration users or first load before sync) → allow through
  if (!user?.role) return <>{children}</>;

  // Wrong role → redirect
  if (user.role !== role) {
    return <Navigate to={redirectTo} replace state={{ roleBlocked: true, requiredRole: role }} />;
  }

  return <>{children}</>;
};
